import React, { useState, useRef, useEffect, useMemo } from 'react';
import { KanaPopover } from '../KanaPopover';
import { MobileKanaSheet } from './MobileKanaSheet';
import { GOJUON_DATA, KATAKANA_DATA, KanaInfo } from './data';
import { KanaGroup } from './KanaGroup';
import './style.css';

interface GojuonChartProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'seion' | 'dakuon' | 'yoon';

export const GojuonChart: React.FC<GojuonChartProps> = ({ isOpen, onClose }) => {
  const [selectedKana, setSelectedKana] = useState<KanaInfo | null>(null);
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);
  const [showRomaji, setShowRomaji] = useState(true);
  const [displayMode, setDisplayMode] = useState<'both' | 'hiragana' | 'katakana'>('both');
  const [activeTab, setActiveTab] = useState<TabType>('seion');
  const [isMobile, setIsMobile] = useState(false);

  // Map to store refs for each kana cell
  const kanaRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get current data based on display mode
  const currentData = displayMode === 'both'
    ? GOJUON_DATA
    : (displayMode === 'hiragana' ? GOJUON_DATA : KATAKANA_DATA);

  // Helper to get corresponding kana
  const getCorrespondingKana = (kana: KanaInfo): string => {
    if (displayMode !== 'both') return '';

    const index = GOJUON_DATA.findIndex(item =>
      item.romaji === kana.romaji &&
      item.type === kana.type &&
      item.position.group === kana.position.group &&
      item.position.period === kana.position.period
    );

    if (index !== -1) {
      const katakana = KATAKANA_DATA.find(item =>
        item.romaji === kana.romaji &&
        item.type === kana.type &&
        item.position.group === kana.position.group &&
        item.position.period === kana.position.period
      );
      return katakana?.kana || '';
    }
    return '';
  };

  const handleKanaClick = (kana: KanaInfo) => {
    const element = kanaRefs.current.get(kana.kana);

    if (selectedKana?.kana === kana.kana) {
      setSelectedKana(null);
      setSelectedElement(null);
    } else {
      setSelectedKana(kana);
      setSelectedElement(element || null);
    }
  };

  const handleClosePopover = () => {
    setSelectedKana(null);
    setSelectedElement(null);
  };

  // Filter periods based on active tab
  const filteredPeriods = useMemo(() => {
    const allPeriods = Array.from(new Set(currentData.map(k => k.position.period))).sort((a, b) => a - b);

    if (activeTab === 'seion') {
      return allPeriods.filter(p => p <= 10); // あ行 - わ行
    } else if (activeTab === 'dakuon') {
      // Periods that contain dakuon/handakuon: Ka(2), Sa(3), Ta(4), Ha(6)
      return [2, 3, 4, 6];
    } else {
      // Yoon
      return [11, 12];
    }
  }, [currentData, activeTab]);

  if (!isOpen) return null;

  const getPeriodTitle = (period: number) => {
    if (activeTab === 'dakuon') {
      switch (period) {
        case 2: return 'が行';
        case 3: return 'ざ行';
        case 4: return 'だ行';
        case 6: return 'ば行 / ぱ行';
        default: return '';
      }
    }

    switch (period) {
      case 1: return 'あ行';
      case 2: return 'か行';
      case 3: return 'さ行';
      case 4: return 'た行';
      case 5: return 'な行';
      case 6: return 'は行';
      case 7: return 'ま行';
      case 8: return 'や行';
      case 9: return 'ら行';
      case 10: return 'わ行';
      case 11: return '拗音 (1)';
      case 12: return '拗音 (2)';
      default: return '';
    }
  };

  return (
    <>
      <div className="gojuon-overlay" onClick={onClose} />
      <div className={`gojuon-modal ${!isMobile && selectedKana ? 'sidebar-open' : ''}`}>
        <div className="gojuon-header">
          <div className="header-top">
            <h2 className="gojuon-title">五十音図</h2>
            <div className="gojuon-controls">
              <div className="control-group switch-group">
                <label className="romaji-switch">
                  <input
                    type="checkbox"
                    checked={showRomaji}
                    onChange={(e) => setShowRomaji(e.target.checked)}
                  />
                  <span className="switch-slider"></span>
                  <span className="switch-label">罗马音</span>
                </label>
              </div>
              <div className="control-group">
                <select
                  value={displayMode}
                  onChange={(e) => setDisplayMode(e.target.value as any)}
                  className="mode-select"
                >
                  <option value="both">平/片</option>
                  <option value="hiragana">平假名</option>
                  <option value="katakana">片假名</option>
                </select>
              </div>
              <button className="close-chart-btn" onClick={onClose}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="gojuon-tabs">
            <button
              className={`tab-btn ${activeTab === 'seion' ? 'active' : ''}`}
              onClick={() => setActiveTab('seion')}
            >
              清音
            </button>
            <button
              className={`tab-btn ${activeTab === 'dakuon' ? 'active' : ''}`}
              onClick={() => setActiveTab('dakuon')}
            >
              浊音/半浊音
            </button>
            <button
              className={`tab-btn ${activeTab === 'yoon' ? 'active' : ''}`}
              onClick={() => setActiveTab('yoon')}
            >
              拗音
            </button>
          </div>
        </div>

        <div className="gojuon-content">
          <div className={`gojuon-scroll-area ${activeTab}`}>
            {filteredPeriods.map(period => (
              <KanaGroup
                key={`${period}-${activeTab}`}
                title={getPeriodTitle(period)}
                kanas={currentData.filter(k => k.position.period === period)}
                onKanaClick={handleKanaClick}
                selectedKana={selectedKana}
                showRomaji={showRomaji}
                displayMode={displayMode}
                getCorrespondingKana={getCorrespondingKana}
                kanaRefs={kanaRefs}
                filterType={activeTab}
              />
            ))}
          </div>
        </div>

        {/* Mobile Bottom Sheet */}
        {isMobile && (
          <MobileKanaSheet
            kanaInfo={selectedKana}
            onClose={handleClosePopover}
            displayMode={displayMode}
            correspondingKana={selectedKana && displayMode === 'both' ? getCorrespondingKana(selectedKana) : undefined}
          />
        )}

        {/* Desktop Popover */}
        {!isMobile && selectedKana && selectedElement && (
          <KanaPopover
            kanaInfo={selectedKana}
            triggerElement={selectedElement}
            onClose={handleClosePopover}
            displayMode={displayMode}
            correspondingKana={displayMode === 'both' ? getCorrespondingKana(selectedKana) : undefined}
          />
        )}
      </div>
    </>
  );
};